import useAgentHubCommission from 'agentHub/hooks/useAgentHubCommissionV2'
import FormatCurrency from 'components/Common/FormatCurrency'
import PriceRowCommission from 'LuxKit/PricePoints/PriceRowCommission'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import React, { useMemo } from 'react'

type PriceRowCommissionProps = React.ComponentProps<typeof PriceRowCommission>

interface Props {
  size: PriceRowCommissionProps['size']
  productType: App.ProductType
  kind?: 'default' | 'plain'
  offerId: string
  packageId?: string
  offerLocations?: Array<string>
  vendorName?: string
  offerTotal?: number
  /**
   * optional, should be used when there are multiple options inside offer
   * (hotel packages, tour variations, etc.) so that we can get a date range commission
   */
  optionId?: string
  offer?: App.AgentHubOfferPayload
}

function PriceRowAgentHubCommission(props: Props) {
  const {
    size,
    productType,
    kind = 'default',
    offerId,
    offerTotal = 0,
    packageId,
    optionId,
    offer,
  } = props

  const customerView = useAppSelector(state => state.agentHub.customerView)
  const { commissionPercentage, packagesCommission, greatestPackageCommission, minBaseCommission, maxBaseCommission, dateRangesCommission } = useAgentHubCommission(offerId, offer)
  const commissionLoading = useAppSelector(state => state.agentHub.offersCommissionLoading[offerId])
  const packageCommission = packageId ? packagesCommission.find(pkg => pkg.packageId === packageId) : null
  const dateRangeCommission = optionId ? dateRangesCommission[optionId] : null

  const greatestCommissionWithDateRange = useMemo(() => {
    const greatestDateRangeCommission = Math.max(0, ...Object.values(dateRangesCommission).map(commission => commission.commissionPercentage))
    return greatestDateRangeCommission + commissionPercentage
  }, [dateRangesCommission, commissionPercentage])

  const commissionToDisplay = useMemo(() => {
    const dateRangeCommissionPercentage = dateRangeCommission?.commissionPercentage ?? 0
    if (packageCommission) {
      return packageCommission.commissionPercentage + dateRangeCommissionPercentage
    }
    return commissionPercentage + dateRangeCommissionPercentage
  }, [dateRangeCommission, commissionPercentage, packageCommission])

  if (config.agentHub.isEnabled && commissionLoading && !customerView) {
    return <TextLoadingBox typography="body-medium" width="8ch" />
  }

  if (!config.agentHub.isEnabled || (!commissionPercentage && !greatestPackageCommission) || !productType || customerView) {
    return null
  }

  if (kind === 'plain') {
    return <>
      {offerTotal > 0 && Boolean(packageCommission?.commissionPercentage) && (
        <BodyTextBlock weight="bold" variant="small" colour="highlight-secondary">
          Earn <FormatCurrency value={offerTotal * ((commissionToDisplay) / 100)} /> commission ({commissionToDisplay}%)
        </BodyTextBlock>
      )}
      {offerTotal > 0 && !packageCommission?.commissionPercentage && (
        <BodyTextBlock weight="bold" variant="small" colour="highlight-secondary">
          Earn <FormatCurrency value={offerTotal * ((commissionToDisplay) / 100)} /> commission ({commissionToDisplay}%)
        </BodyTextBlock>
      )}
      {offerTotal === 0 && (
        <BodyTextBlock weight="bold" variant="small" colour="highlight-secondary">
          Earn {commissionToDisplay}% commission
        </BodyTextBlock>
      )}
    </>
  }

  return <PriceRowCommission
    size={size}
    commissionPercentage={commissionToDisplay}
    productType={productType}
    offerTotal={offerTotal}
    multipleCommissions={Boolean(greatestPackageCommission) || Object.keys(dateRangesCommission).length > 0}
    greatestCommission={Math.max(greatestPackageCommission, greatestCommissionWithDateRange, commissionToDisplay)}
    minBaseCommission={minBaseCommission}
    maxBaseCommission={maxBaseCommission}
  />
}

export default PriceRowAgentHubCommission
